import { Component, forwardRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { RadioState } from '@app/core/states/radio.state';
import {
  dateOrNumberOperators,
  PlaylistCriteriaDirective,
} from '@app/library/components/sides/playlist-criteria/playlist-criteria.directive';
import { Criterion, operators } from '@app/library/models/criterias.model';
import { Store } from '@ngxs/store';
import { FormHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import moment from 'moment-timezone';

@Component({
  selector: 'rk-playlist-criteria-date',
  templateUrl: './playlist-criteria-date.component.html',
  styleUrls: ['./playlist-criteria-date.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PlaylistCriteriaDateComponent),
      multi: true,
    },
  ],
})
export class PlaylistCriteriaDateComponent
  extends PlaylistCriteriaDirective
  implements OnInit, ControlValueAccessor
{
  timezone: string;
  formattedBetweenValue = '';

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly store: Store,
    formHelper: FormHelperService,
  ) {
    super(formHelper);
  }

  ngOnInit() {
    super.ngOnInit();
    this.criteriaForm = this.fb.group({
      field: '',
      operator: dateOrNumberOperators[0],
      value: '',
      betweenValue: '',
    });
    this.timezone = this.store.selectSnapshot(RadioState.currentRadioTimezone);
  }

  isBetweenInput() {
    return (
      this.criteriaForm.get('operator').value &&
      this.criteriaForm.get('operator').value.name === operators.BETWEEN
    );
  }

  shouldReset(prevOp: operators, op: operators): boolean {
    if (prevOp === op) {
      return false;
    }

    return (
      (prevOp === operators.BETWEEN && op !== operators.BETWEEN) ||
      (prevOp !== operators.BETWEEN && op === operators.BETWEEN)
    );
  }

  formValueToCriteria(formData: any): Criterion {
    const shouldReset = this.shouldReset(formData.operator.id, this.prevOperator['id']);
    this.prevOperator = formData.operator;
    if (shouldReset) {
      this.criteriaForm.clearValidators();
      this.criteriaForm.get('value').setValue('');
      this.criteriaForm.get('betweenValue').setValue('');
      this.formattedBetweenValue = '';
    }

    if (this.isBetweenInput()) {
      let value;
      if (!shouldReset) {
        if (
          formData.betweenValue &&
          formData.betweenValue.startDate &&
          formData.betweenValue.endDate
        ) {
          value = {
            from: this.formatDate(formData.betweenValue.startDate),
            to: this.formatDate(formData.betweenValue.endDate),
          };
          this.formatBetweenValue(
            formData.betweenValue.startDate,
            formData.betweenValue.endDate,
          );
        }
      }

      return {
        field: formData.field,
        operator: formData.operator['id'],
        value,
      };
    }

    return {
      field: formData.field,
      operator: formData.operator['id'],
      value:
        shouldReset || !formData.value || !formData.value.endDate
          ? ''
          : this.formatDate(formData.value.endDate),
    };
  }

  formatDate(date: moment.Moment): string {
    return moment.tz(date, this.timezone).startOf('day').format();
  }

  criteriaToFormValue(val: any): any {
    const ret = {
      ...val,
      operator: this.findOperatorWithId(val.operator),
      betweenValue: '',
    };
    this.prevOperator = ret.operator;

    if (val.value['from']) {
      ret.betweenValue = {
        startDate: moment(val.value['from']),
        endDate: moment(val.value['to']),
      };
    } else if (val.value) {
      delete ret.value;
      ret.value = { startDate: moment(val.value), endDate: moment(val.value) };
    }

    return ret;
  }

  findOperatorWithId(name: string) {
    return dateOrNumberOperators.find(operator => operator.id === name);
  }

  generateOperatorsInput(): SugestionInput[] {
    return dateOrNumberOperators;
  }

  registerOnChange(fn: any): void {
    this.criteriaForm.valueChanges.subscribe(val => fn(this.formValueToCriteria(val)));
  }

  writeValue(value: any): void {
    if (!value) {
      return;
    }
    this.criteriaForm.setValue(this.criteriaToFormValue(value), { emitEvent: false });

    if (value.operator === operators.BETWEEN && value.value) {
      setTimeout(() =>
        this.formatBetweenValue(moment(value.value.from), moment(value.value.to)),
      );
    }
  }

  formatBetweenValue(from: moment.Moment, to: moment.Moment): void {
    const desiredFormat = moment.localeData().longDateFormat('ll');
    const fromFormatted = from.format(desiredFormat);
    const toFormatted = to.format(desiredFormat);
    this.formattedBetweenValue = `${fromFormatted} - ${toFormatted}`;
  }
}
